import { useState, useRef, useEffect } from 'react'
import { X, ChevronDown, Search } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface MultiSelectOption {
  id: string
  label: string
  email?: string
}

interface MultiSelectProps {
  options: MultiSelectOption[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  className?: string
  disabled?: boolean
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = 'Seleccionar...',
  searchPlaceholder = 'Buscar...',
  className,
  disabled = false,
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filteredOptions = options.filter((option) => {
    const search = searchTerm.toLowerCase()
    return (
      option.label.toLowerCase().includes(search) ||
      option.email?.toLowerCase().includes(search)
    )
  })

  const selectedOptions = options.filter((opt) => selected.includes(opt.id))

  const toggleOption = (optionId: string) => {
    if (disabled) return

    if (selected.includes(optionId)) {
      onChange(selected.filter((id) => id !== optionId))
    } else {
      onChange([...selected, optionId])
      // Cerrar el dropdown después de seleccionar un elemento
      setIsOpen(false)
      setSearchTerm('')
    }
  }

  const removeOption = (optionId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (disabled) return
    onChange(selected.filter((id) => id !== optionId))
  }

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      {/* Selected tags display */}
      <div
        className={cn(
          'min-h-[42px] w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm',
          'flex flex-wrap items-center gap-2',
          'focus-within:ring-2 focus-within:ring-[#075E54] focus-within:ring-offset-2',
          disabled && 'bg-gray-50 cursor-not-allowed opacity-60',
          !isOpen && 'cursor-pointer'
        )}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {selectedOptions.length === 0 ? (
          <span className="text-gray-500">{placeholder}</span>
        ) : (
          selectedOptions.map((option) => (
            <span
              key={option.id}
              className="inline-flex items-center gap-1 rounded-md bg-[#075E54] px-2 py-1 text-xs text-white"
            >
              {option.label}
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => removeOption(option.id, e)}
                  className="ml-1 rounded-full hover:bg-[#075E54]/80"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))
        )}
        <ChevronDown
          className={cn(
            'ml-auto h-4 w-4 text-gray-400 transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-300 bg-white shadow-lg max-h-[280px] flex flex-col">
          {/* Search input */}
          <div className="border-b border-gray-200 p-2 flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-md border border-gray-300 py-2 pl-8 pr-3 text-sm focus:border-[#075E54] focus:outline-none focus:ring-1 focus:ring-[#075E54]"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setIsOpen(false)
                    setSearchTerm('')
                  }
                }}
              />
            </div>
          </div>

          {/* Options list */}
          <div className="overflow-auto p-1 flex-1" style={{ maxHeight: '240px' }}>
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">No se encontraron resultados</div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selected.includes(option.id)
                return (
                  <div
                    key={option.id}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-gray-100',
                      isSelected && 'bg-[#075E54]/10'
                    )}
                    onClick={() => toggleOption(option.id)}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleOption(option.id)}
                      className="h-4 w-4 rounded border-gray-300 text-[#075E54] focus:ring-[#075E54]"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{option.label}</div>
                      {option.email && (
                        <div className="text-xs text-gray-500">{option.email}</div>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}

